import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { getPortalSla } from "@/lib/portal-sla.functions";
import { PortalPageHeader } from "@/components/portal-shell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Clock, ShieldCheck, AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/sla")({ component: PortalSlaPage });

function PortalSlaPage() {
  const fetchFn = useServerFn(getPortalSla);
  const [days, setDays] = useState("90");
  const { data, isLoading } = useQuery({
    queryKey: ["portal-sla", days],
    queryFn: () => fetchFn({ data: { days: parseInt(days, 10) } }),
  });

  return (
    <>
      <PortalPageHeader
        title="Service Performance"
        description="On-time shipping and order accuracy for your account."
      />
      <div className="p-4 sm:p-6 space-y-6">
        <div className="flex justify-end">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 180 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between"><CardTitle className="text-sm text-muted-foreground">On-Time Shipping</CardTitle><Clock className="h-4 w-4 text-muted-foreground" /></CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold tabular-nums">{isLoading ? "—" : (data?.on_time_pct != null ? `${data.on_time_pct}%` : "—")}</div>
              <div className="text-xs text-muted-foreground mt-1">{data ? `${data.on_time} on-time · ${data.late} late` : ""}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between"><CardTitle className="text-sm text-muted-foreground">Order Accuracy</CardTitle><ShieldCheck className="h-4 w-4 text-muted-foreground" /></CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold tabular-nums">{isLoading ? "—" : (data?.accuracy_pct != null ? `${data.accuracy_pct}%` : "—")}</div>
              <div className="text-xs text-muted-foreground mt-1">{data ? `${data.accurate} complete · ${data.short} short` : ""}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between"><CardTitle className="text-sm text-muted-foreground">Open Orders</CardTitle><AlertTriangle className="h-4 w-4 text-muted-foreground" /></CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold tabular-nums">{isLoading ? "—" : (data?.open ?? 0)}</div>
              <div className="text-xs text-muted-foreground mt-1">Awaiting shipment</div>
            </CardContent>
          </Card>
        </div>

        <div className="border rounded-lg bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Order</TableHead>
              <TableHead>Requested by</TableHead>
              <TableHead>Shipped</TableHead>
              <TableHead>On-time</TableHead>
              <TableHead>Complete</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : data?.recent && data.recent.length > 0 ? data.recent.map((r) => (
                <TableRow key={r.order_number}>
                  <TableCell className="font-mono text-sm">{r.order_number}</TableCell>
                  <TableCell className="text-muted-foreground">{r.due ? new Date(r.due).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{r.shipped_at ? new Date(r.shipped_at).toLocaleDateString() : "—"}</TableCell>
                  <TableCell>{r.on_time == null ? <span className="text-muted-foreground">—</span> : r.on_time ? <Badge>On time</Badge> : <Badge variant="destructive">Late</Badge>}</TableCell>
                  <TableCell>{r.accurate == null ? <span className="text-muted-foreground">—</span> : r.accurate ? <Badge>Complete</Badge> : <Badge variant="secondary">Short</Badge>}</TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No shipped orders in this window.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}